import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { useGetClientNewsletter, usePostBlob } from '~/hooks';
import { ROUTES } from '~/utils/routes';
import { MAX_ATTACHMENT_FILES, PUBLISH_TYPE } from '../constants';
import { convertToInitialData } from '../logics/converter';
import { generatePatchRequestBody } from '../logics/generatePatchRequestBody';
import { type EditInternalNewsFormValues, editInternalNewsSchema } from '../logics/schema';
import { EditInternalNewsPresentation } from '../presentations';
import { usePatchClientNewsletter } from './usePatchClientNewsletter';

type Args = {
  newsId: number;
};

type ExistingAttachment = {
  blobSignedId: string;
  name: string;
  url: string;
};

type InitialValues = {
  title: string;
  body: string;
  hadMainImage: boolean;
  mainImageUrl?: string;
  isPublished: boolean;
  publishMode: (typeof PUBLISH_TYPE)[keyof typeof PUBLISH_TYPE];
  scheduledDate: Date | undefined;
  scheduledTime: string | undefined;
  existingAttachments: ExistingAttachment[];
};

type PresentationProps = React.ComponentProps<typeof EditInternalNewsPresentation>;

type IUseEditInternalNews = (args: Args) => {
  state: {
    methods: UseFormReturn<EditInternalNewsFormValues>;
    initialValues: InitialValues;
    isMutating: boolean;
    publishMode: (typeof PUBLISH_TYPE)[keyof typeof PUBLISH_TYPE];
    existingAttachments: ExistingAttachment[];
    files: PresentationProps['attachedFiles'];
    isFileUploading: PresentationProps['isFileUploading'];
    isFileUploadDisabled: PresentationProps['isFileUploadDisabled'];
    mainImageUrl: string | undefined;
  };
  actions: {
    handleSubmit: () => void;
    handleCancel: () => void;
    handleRemoveExistingAttachment: (blobSignedId: string) => void;
    handleRemoveMainImage: () => void;
    handleFilesChange: PresentationProps['onFilesChange'];
  };
};

export const useEditInternalNews: IUseEditInternalNews = ({ newsId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();
  const threeLetterCode = getThreeLetterCode() || '';

  // GET: 記事詳細を取得
  const { data } = useGetClientNewsletter({ threeLetterCode, id: newsId });
  const initialValues = convertToInitialData(data.client_newsletter);

  // 既存添付ファイルの管理（削除された既存ファイルの blobSignedId を管理）
  const [removedAttachmentBlobSignedIds, setRemovedAttachmentBlobSignedIds] = useState<string[]>(
    []
  );
  const existingAttachments = initialValues.existingAttachments.filter(
    a => !removedAttachmentBlobSignedIds.includes(a.blobSignedId)
  );

  // 新規追加ファイルの管理（RHF 外で管理）
  const [files, setFiles] = useState<File[]>([]);
  // File → signed_id のマッピング。parallel array の index ずれを防ぐため Map で管理する
  const fileSignedIdMapRef = useRef<Map<File, string>>(new Map());
  // アップロード中に削除されたファイルを追跡する Set。onSuccess でのレースコンディション防止用
  const cancelledFilesRef = useRef<Set<File>>(new Set());

  // フォームの初期値を反映
  const methods = useForm<EditInternalNewsFormValues>({
    defaultValues: {
      title: initialValues.title,
      body: initialValues.body,
      mainImage: undefined,
      publishMode: initialValues.publishMode,
      scheduledDate: initialValues.scheduledDate,
      scheduledTime: initialValues.scheduledTime ?? '',
    },
    criteriaMode: 'all',
    resolver: zodResolver(editInternalNewsSchema),
    mode: 'onBlur',
  });

  const publishMode = methods.watch('publishMode');

  // メイン画像の削除を追跡するフラグ。
  // mainImage の defaultValue も undefined のため、"変更なし" と "削除" を区別するために使用する。
  // X ボタンを押したら新規選択のキャンセルか否かを問わず削除扱いとする。
  // isMainImageRemovedRef: submit 時の同期アクセス用
  // isMainImageRemoved: プレビュー URL の表示制御用（削除済みなら defaultPreviewUrl を非表示にする）
  const isMainImageRemovedRef = useRef(false);
  const [isMainImageRemoved, setIsMainImageRemoved] = useState(false);

  // PATCH: 記事を更新
  const { trigger, isMutating } = usePatchClientNewsletter(threeLetterCode, newsId, {
    onSuccess: () => {
      showToast({ message: '社内報を更新しました。', status: 'success' });
      navigate(ROUTES.INTERNAL_NEWS.INDEX);
    },
    onError: () => {
      showToast({ message: '社内報の更新に失敗しました。', status: 'danger' });
    },
  });

  // Blob アップロード: 新規添付ファイルを都度アップロードして signed_id を取得する
  const { trigger: triggerBlob, isMutating: isFileMutating } = usePostBlob(threeLetterCode);

  const handleSubmit = methods.handleSubmit(async formValues => {
    const executeSubmit = async () => {
      const requestBody = await generatePatchRequestBody({
        formValues,
        hadMainImage: initialValues.hadMainImage,
        isMainImageRemoved: isMainImageRemovedRef.current,
        originalAttachments: initialValues.existingAttachments,
        remainingAttachments: existingAttachments,
        newFileSignedIds: [...fileSignedIdMapRef.current.values()],
        isPublished: initialValues.isPublished,
      }).catch(() => {
        showToast({
          message: '添付ファイルの処理に失敗したため、社内報を更新できませんでした。',
          status: 'danger',
        });
      });

      if (!requestBody) return;
      await trigger(requestBody);
    };

    if (formValues.publishMode === PUBLISH_TYPE.IMMEDIATELY && !initialValues.isPublished) {
      showAlertDialog({
        title: '今すぐ公開しますか？',
        message: '現在の時刻で社内報が投稿され、利用者にプッシュ通知での案内が届きます。',
        confirmText: '公開する',
        onConfirm: executeSubmit,
      });
    } else {
      await executeSubmit();
    }
  });

  const handleCancel = () => navigate(ROUTES.INTERNAL_NEWS.INDEX);

  const handleRemoveExistingAttachment = (blobSignedId: string) => {
    setRemovedAttachmentBlobSignedIds(prev => [...prev, blobSignedId]);
  };

  const handleRemoveMainImage = () => {
    isMainImageRemovedRef.current = true;
    setIsMainImageRemoved(true);
  };

  /**
   * ファイル変更ハンドラ。
   * 追加されたファイルをアップロードして signed_id を Map に追加し、
   * 削除されたファイルの signed_id を Map から除去する。
   * 既存ファイルと新規追加ファイルの合計が MAX_ATTACHMENT_FILES を超えた場合は早期リターンする。
   */
  const handleFilesChange = (newFiles: File[]) => {
    // 既存ファイルと新規追加ファイルの合計が件数上限を超えた場合はファイルを追加しない
    if (existingAttachments.length + newFiles.length > MAX_ATTACHMENT_FILES) return;

    // 削除されたファイルを Map から除去する。
    // アップロード中のファイルは Map に未登録のため、cancelledFilesRef で追跡して
    // onSuccess でのレースコンディションを防ぐ
    for (const file of [...fileSignedIdMapRef.current.keys()]) {
      if (!newFiles.includes(file)) {
        fileSignedIdMapRef.current.delete(file);
      }
    }
    for (const file of files) {
      if (!newFiles.includes(file)) {
        cancelledFilesRef.current.add(file);
      }
    }

    // 追加されたファイルをアップロードする
    for (const file of newFiles.filter(f => !files.includes(f))) {
      triggerBlob(
        { 'blob[file]': file },
        {
          onSuccess: data => {
            // 削除済みファイルの signed_id は追加しない（レースコンディション対策）
            if (cancelledFilesRef.current.has(file)) {
              cancelledFilesRef.current.delete(file);
              return;
            }
            fileSignedIdMapRef.current.set(file, data.blob.signed_id);
          },
          onError: () => {
            cancelledFilesRef.current.delete(file);
            showToast({ message: 'ファイルのアップロードに失敗しました。', status: 'danger' });
            setFiles(prevFiles => prevFiles.filter(prevFile => prevFile !== file));
          },
        }
      );
    }

    setFiles(newFiles);
  };

  return {
    state: {
      methods,
      initialValues,
      isMutating,
      publishMode,
      existingAttachments,
      files,
      isFileUploading: isFileMutating,
      isFileUploadDisabled:
        isFileMutating || existingAttachments.length + files.length >= MAX_ATTACHMENT_FILES,
      mainImageUrl: isMainImageRemoved ? undefined : initialValues.mainImageUrl,
    },
    actions: {
      handleSubmit,
      handleCancel,
      handleRemoveExistingAttachment,
      handleRemoveMainImage,
      handleFilesChange,
    },
  };
};
